import { useEffect } from 'react';
import { apolloClient } from '../api/apolloClient';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/WebsocketContext';
import { GET_MY_SESSIONS } from '../graphql/resolvers/sessions/sessions.gql';
import { shouldLogoutForRevokedSession } from '../helpers/sessionRevocation';

// TRIL-381: how often to re-check while the app stays open. A fallback for when
// the WS push (TRIL-385) didn't reach the tab (offline, or a frozen tab that
// missed the message). Browsers throttle/freeze this in hidden tabs, which is
// exactly why the WS push + visibilitychange checks exist.
const POLL_MS = 3 * 60 * 1000;

/**
 * Effective remote logout. Revoking a session deletes its refresh-token row, but
 * the browser's access token stays valid until it expires — so a revoked browser
 * would keep working. This logs it out through three complementary triggers:
 *
 * - **WS push (TRIL-385, primary):** the server broadcasts `session_revoked` with
 *   the revoked sessionIds; if ours is among them we log out immediately — reaches
 *   a *live* backgrounded tab instantly, no poll wait.
 * - **visibilitychange / focus (TRIL-381/385):** re-check the moment the tab
 *   becomes visible again — catches a frozen tab that missed the WS message.
 * - **mount + 3-min poll (TRIL-381):** reload + a throttled-but-present fallback.
 *
 * The check asks the server which sessions are still live; if THIS browser's
 * stored `sessionId` is absent from a **non-empty** list it was signed out
 * elsewhere → logout. A single retry absorbs the brief post-login consistency
 * window so a freshly-logged-in session is never kicked.
 */
export function useSessionRevocationGuard(): void {
  const { isSignedIn, logout } = useAuth();
  const { onEvent } = useSocket();

  // Poll + visibility/focus fallback.
  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;

    const fetchSessions = async () => {
      const { data } = await apolloClient.query({
        query: GET_MY_SESSIONS,
        fetchPolicy: 'network-only',
      });
      return data?.mySessions ?? [];
    };

    const check = async () => {
      const sid = window.localStorage.getItem('sessionId');
      if (!sid) return; // legacy session with no id — can't verify, leave as-is
      try {
        let sessions = await fetchSessions();
        if (!shouldLogoutForRevokedSession(sessions, sid)) return;
        // Absent once → retry after a beat to rule out consistency lag post-login.
        await new Promise((resolve) => setTimeout(resolve, 2500));
        if (cancelled) return;
        sessions = await fetchSessions();
        if (!cancelled && shouldLogoutForRevokedSession(sessions, sid)) {
          await logout();
        }
      } catch {
        // network/transient error → leave the session as-is
      }
    };

    void check();
    const onFocus = () => void check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(() => void check(), POLL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [isSignedIn, logout]);

  // TRIL-385: instant logout via WS push. The server broadcasts the revoked
  // sessionIds to all the user's connections; each browser logs out only if its
  // own stored sessionId is in the list (so the acting device ignores it).
  useEffect(() => {
    if (!isSignedIn) return;
    const unsubscribe = onEvent(
      'session_revoked',
      (data: { sessionIds?: string[] }) => {
        const sid = window.localStorage.getItem('sessionId');
        const revoked = data?.sessionIds ?? [];
        if (sid && revoked.includes(sid)) {
          void logout();
        }
      },
    );
    return unsubscribe;
  }, [isSignedIn, logout, onEvent]);
}
